import React from 'react';

/**
 * Parses bold text (**text**) inside a line and returns an array of React elements or text strings.
 */
export function parseInlineFormatting(text: string): React.ReactNode {
  if (!text) return '';
  
  // Split using regex group to capture the bold markers
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  
  return parts.map((part, index) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      const boldText = part.slice(2, -2);
      return (
        <strong key={index} className="font-extrabold text-zinc-900">
          {boldText}
        </strong>
      );
    }
    return part;
  });
}

/**
 * Converts basic markdown formatting (bold, lists, line breaks) into safe React elements.
 * Groups consecutive list items (* or •) into a single <ul> block.
 */
export function parseMarkdownToReact(text: string): React.ReactNode[] {
  if (!text) return [];

  const lines = text.split('\n');
  const elements: React.ReactNode[] = [];
  
  let currentListItems: React.ReactNode[] = [];
  let listKey = 0;

  const pushCurrentList = () => {
    if (currentListItems.length > 0) {
      elements.push(
        <ul key={`list-${listKey++}`} className="list-disc pl-5 my-2.5 space-y-1 text-slate-900">
          {currentListItems}
        </ul>
      );
      currentListItems = [];
    }
  };

  lines.forEach((line, index) => {
    // Dá match em • ou - ou em * APENAS se NÃO for seguido por outro * (evita fatiar **negrito**)
    const listMatch = line.match(/^\s*(?:[•\-]|(?:\*(?!\*)))\s*(.*)/);

    if (listMatch) {
      const content = listMatch[1];
      currentListItems.push(
        <li key={`li-${index}`} className="text-sm font-medium leading-relaxed">
          {parseInlineFormatting(content)}
        </li>
      );
    } else {
      pushCurrentList();

      const trimmedLine = line.trim();
      if (trimmedLine === '') {
        // Preserve visual line breaks with small spacer divs
        elements.push(<div key={`br-${index}`} className="h-2" />);
      } else {
        elements.push(
          <p key={`p-${index}`} className="text-sm font-medium text-slate-900 leading-relaxed whitespace-pre-wrap">
            {parseInlineFormatting(line)}
          </p>
        );
      }
    }
  });

  pushCurrentList();

  return elements;
}
